/**
 * Universal Agent Dashboard Component
 * 
 * This component displays all external API requests across all templates (legal, support, homeimprovement)
 * for universal agents. It integrates with the Universal External API endpoints to fetch and manage requests.
 * 
 * Required Props:
 * - organizationId: string - Organization ID for API requests
 * - apiKey: string - API key for authentication (should come from auth context/environment)
 * 
 * Features:
 * - Real-time request fetching via /api/v1/universal/requests
 * - Accept requests via /api/v1/universal/accept/{sessionId}  
 * - Filter by template type, status, and urgency
 * - Auto-refresh every 30 seconds
 * - Real-time wait time calculations
 * 
 * API Integration:
 * - No hardcoded credentials - requires proper props
 * - Uses configurable API key for authentication
 * - Handles errors gracefully with empty states
 * - Redirects to appropriate template video sessions upon acceptance
 * 
 * API Integration Setup:
 * curl -X POST https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/external/generate-session-url \
 *   -H "Authorization: Bearer YOUR_API_KEY" \
 *   -H "Content-Type: application/json" \
 *   -d '{
 *     "template_type": "universal",
 *     "participant_data": {
 *       "customer_name": "UI Test Customer",
 *       "customer_email": "uitest@example.com",
 *       "agent_name": "Universal Agent"
 *     },
 *     "project_details": {
 *       "title": "UI Separation Test Session",
 *       "category": "technical-support",
 *       "urgency": "high",
 *       "description": "Testing separate UI for host vs customer in universal template"
 *     },
 *     "organization_config": {
 *       "company_name": "Test Company"
 *     },
 *     "metadata": {
 *       "source": "UI Test",
 *       "environment": "development"
 *     }
 *   }'
 * 
 * Integrate the Universal External API with Zapier, webhooks, or custom applications 
 * to automatically route requests to your universal agent dashboard.
 */
import React, { useState, useEffect } from 'react';
import { 
  Scale,
  Headphones,
  Home,
  Clock, 
  User, 
  AlertCircle,
  CheckCircle,
  Video,
  Filter,
  Search,
  RefreshCw,
  TrendingUp,
  Phone,
  Mail,
  MessageSquare,
  Timer,
  Star,
  MoreVertical,
  ChevronDown,
  Activity,
  Users,
  Calendar,
  Zap,
  Globe,
  ExternalLink
} from 'lucide-react';
import { BrandingProvider } from '../../contexts/BrandingContext';
import { 
  BrandedHeader, 
  BrandedButton, 
  BrandedCard, 
  BrandedBadge,
  BrandedInput,
  BrandedSelect
} from '../../components/branding/BrandedComponents';
import platformApi from '../../services/platformApi';

interface UniversalRequest {
  sessionId: string;
  requestId: string;
  templateType: 'legal' | 'support' | 'homeimprovement' | 'universal';
  status: 'waiting' | 'accepted' | 'active' | 'completed' | 'expired' | 'cancelled';
  participantData: {
    name: string;
    email: string;
    phone?: string;
    customer_name?: string;  // API may send this field
    customer_email?: string;  // API may send this field
  };
  projectDetails: {
    title?: string;
    subject?: string;
    case_type?: string;
    category: string;
    urgency?: 'low' | 'medium' | 'high' | 'urgent' | 'planning';
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    description?: string;
    budget?: string;
    address?: string;
  };
  hostData: {
    name: string;
    company: string;
  };
  waitTime: number; // in minutes
  createdAt: string;
  assignedAgentId?: string;
  metadata?: {
    source?: string;
    campaign_id?: string;
  };
}

interface UniversalAgentDashboardProps {
  agentName?: string;
  agentId?: string;
  organizationId: string; // Required - organization ID for the agent
}

export const UniversalAgentDashboard: React.FC<UniversalAgentDashboardProps> = ({
  agentName = 'Universal Agent',
  agentId = 'agent_universal_001',
  organizationId
}) => {
  const [requests, setRequests] = useState<UniversalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<{
    template: 'all' | 'legal' | 'support' | 'homeimprovement';
    status: 'all' | 'waiting' | 'accepted' | 'active';
    urgency: 'all' | 'high' | 'urgent';
  }>({
    template: 'all',
    status: 'waiting',
    urgency: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [acceptingRequest, setAcceptingRequest] = useState<string | null>(null);
  const [rejectingRequest, setRejectingRequest] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalWaiting: 0,
    totalActive: 0,
    avgWaitTime: 0,
    todayHandled: 0
  });

  // Fetch universal requests from platform API (session-based)
  const fetchUniversalRequests = async (): Promise<UniversalRequest[]> => {
    try {
      const response = await platformApi.getUniversalRequests({
        status: filter.status === 'all' ? undefined : filter.status,
        templateType: filter.template === 'all' ? undefined : filter.template,
        limit: 50,
        offset: 0
      });
      
      if (!response.success || !response.requests) {
        console.warn('No requests found or invalid response format');
        return [];
      }

      // Transform API response to match our interface
      return response.requests.map((apiRequest: any): UniversalRequest => {
        // Calculate wait time from created_at
        const createdAt = new Date(apiRequest.created_at);
        const waitTime = Math.max(0, Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60)));
        
        return {
          sessionId: apiRequest.session_id,
          requestId: apiRequest.request_id || apiRequest.session_id,
          templateType: apiRequest.template_type,
          status: apiRequest.status,
          participantData: apiRequest.participant_data,
          projectDetails: apiRequest.project_details,
          hostData: {
            name: apiRequest.organization_config.host_name || agentName,
            company: apiRequest.organization_config.company_name || 'Universal Services'
          },
          waitTime,
          createdAt: apiRequest.created_at,
          assignedAgentId: apiRequest.assigned_agent_id,
          metadata: apiRequest.metadata
        };
      });
    } catch (error) {
      console.error('Error fetching universal requests:', error);
      // Return empty array instead of mock data to avoid confusion
      return [];
    }
  };

  // Get template icon and color
  const getTemplateInfo = (templateType: string) => {
    switch (templateType) {
      case 'legal':
        return { icon: Scale, color: 'blue', name: 'Legal', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' };
      case 'support':
        return { icon: Headphones, color: 'green', name: 'Support', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' };
      case 'homeimprovement':
        return { icon: Home, color: 'orange', name: 'Home', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' };
      default:
        return { icon: MessageSquare, color: 'gray', name: 'General', bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' };
    }
  };

  // Get urgency/priority color
  const getUrgencyColor = (urgency?: string, priority?: string) => {
    const level = urgency || priority || 'medium';
    switch (level) {
      case 'urgent':
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
      case 'planning':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Format wait time
  const formatWaitTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Get request title
  const getRequestTitle = (request: UniversalRequest) => {
    switch (request.templateType) {
      case 'legal':
        return request.projectDetails.case_type || 'Legal Consultation';
      case 'support':
        return request.projectDetails.subject || 'Support Request';
      case 'homeimprovement':
        return request.projectDetails.title || 'Home Project';
      default:
        return 'Consultation Request';
    }
  };

  // Accept request using session-based authentication
  const acceptRequest = async (request: UniversalRequest) => {
    try {
      setAcceptingRequest(request.sessionId);
      
      // Use platformApi service which handles session-based authentication
      const response = await platformApi.acceptUniversalRequest(request.sessionId, {
        agent_id: agentId,
        agent_name: agentName
      });
      
      console.log('[UniversalAgentDashboard] Accept response:', response);
      
      if (!response.success) {
        throw new Error('Failed to accept request');
      }
      
      // Update local state
      setRequests(prev => prev.map(r => 
        r.sessionId === request.sessionId 
          ? { ...r, status: 'accepted', assignedAgentId: agentId }
          : r
      ));
      
      // Redirect to the video session URL
      // Use the room_id from the response if available
      const roomId = response.room_id || `room_${request.sessionId}`;
      const customerName = request.participantData?.customer_name || request.participantData?.name || 'Customer';
      const customerEmail = request.participantData?.customer_email || request.participantData?.email || 'customer@example.com';
      const title = request.projectDetails?.title || 'Universal Session';
      const category = request.projectDetails?.category || 'consultation';
      const urgency = request.projectDetails?.urgency || request.projectDetails?.priority || 'medium';
      const description = request.projectDetails?.description || '';
      
      // Get the current org ID from the URL
      const currentPath = window.location.pathname;
      const orgMatch = currentPath.match(/^\/([a-zA-Z0-9_]+)/);
      const orgPrefix = orgMatch && orgMatch[1].startsWith('org_') ? `/${orgMatch[1]}` : '';
      
      // Construct URL with all required parameters, including org prefix
      const videoSessionUrl = `${orgPrefix}/templates/universal/session/host?roomId=${encodeURIComponent(roomId)}&requestId=${encodeURIComponent(request.requestId)}&agentName=${encodeURIComponent(agentName)}&customerName=${encodeURIComponent(customerName)}&customerEmail=${encodeURIComponent(customerEmail)}&title=${encodeURIComponent(title)}&category=${encodeURIComponent(category)}&urgency=${encodeURIComponent(urgency)}&description=${encodeURIComponent(description)}&participantType=host`;
      
      console.log('[UniversalAgentDashboard] Redirecting to:', videoSessionUrl);
      window.location.href = videoSessionUrl;
      
    } catch (error) {
      console.error('Error accepting request:', error);
      alert(`Failed to accept request: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setAcceptingRequest(null);
    }
  };

  // Reject request
  const rejectRequest = async (request: UniversalRequest) => {
    try {
      setRejectingRequest(request.sessionId);
      
      // Use platformApi service to reject the request
      const response = await platformApi.rejectUniversalRequest(request.sessionId, {
        agent_id: agentId,
        agent_name: agentName,
        rejection_reason: 'Agent declined'
      });
      
      console.log('[UniversalAgentDashboard] Reject response:', response);
      
      if (!response.success) {
        throw new Error('Failed to reject request');
      }
      
      // Remove request from local state (or mark as rejected)
      setRequests(prev => prev.filter(r => r.sessionId !== request.sessionId));
      
      console.log(`[UniversalAgentDashboard] Request rejected: ${request.sessionId}`);
      
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert(`Failed to reject request: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setRejectingRequest(null);
    }
  };

  // Get video session URL based on template type
  const getVideoSessionUrl = (request: UniversalRequest) => {
    // Get the current org ID from the URL
    const currentPath = window.location.pathname;
    const orgMatch = currentPath.match(/^\/([a-zA-Z0-9_]+)/);
    const orgPrefix = orgMatch && orgMatch[1].startsWith('org_') ? `/${orgMatch[1]}` : '';
    
    const baseParams = `roomId=room_${request.sessionId}&requestId=${request.requestId}&agentName=${encodeURIComponent(agentName)}`;
    
    switch (request.templateType) {
      case 'legal':
        return `${orgPrefix}/templates/legal/session/host?${baseParams}&firmName=${encodeURIComponent(request.hostData.company)}`;
      case 'support':
        return `${orgPrefix}/templates/support/session/agent?${baseParams}&companyName=${encodeURIComponent(request.hostData.company)}`;
      case 'homeimprovement':
        return `${orgPrefix}/templates/homeimprovement/session/contractor?${baseParams}&companyName=${encodeURIComponent(request.hostData.company)}`;
      case 'universal':
        // For universal template, include customer info
        const customerName = request.participantData?.customer_name || request.participantData?.name || 'Customer';
        return `${orgPrefix}/templates/universal/session/host?${baseParams}&customerName=${encodeURIComponent(customerName)}&title=${encodeURIComponent(request.projectDetails?.title || 'Universal Session')}`;
      default:
        // Default to universal if template type is not recognized
        return `${orgPrefix}/templates/universal/session/host?${baseParams}`;
    }
  };

  // Filter requests
  const filteredRequests = requests.filter(request => {
    if (filter.template !== 'all' && request.templateType !== filter.template) return false;
    if (filter.status !== 'all' && request.status !== filter.status) return false;
    if (filter.urgency !== 'all') {
      const urgency = request.projectDetails.urgency || request.projectDetails.priority;
      if (!urgency || !['high', 'urgent'].includes(urgency)) return false;
    }
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        request.participantData.name.toLowerCase().includes(searchLower) ||
        getRequestTitle(request).toLowerCase().includes(searchLower) ||
        request.projectDetails.category.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      try {
        // Fetch real data from API
        const requestsData = await fetchUniversalRequests();
        setRequests(requestsData);
        
        // Calculate stats from real data
        const waitingRequests = requestsData.filter(r => r.status === 'waiting');
        const activeRequests = requestsData.filter(r => r.status === 'active');
        const completedToday = requestsData.filter(r => {
          const createdAt = new Date(r.createdAt);
          const today = new Date();
          return createdAt.toDateString() === today.toDateString() && r.status === 'completed';
        });
        
        const avgWait = waitingRequests.length > 0 
          ? Math.round(waitingRequests.reduce((sum, r) => sum + r.waitTime, 0) / waitingRequests.length)
          : 0;
        
        setStats({
          totalWaiting: waitingRequests.length,
          totalActive: activeRequests.length,
          avgWaitTime: avgWait,
          todayHandled: completedToday.length
        });
      } catch (error) {
        console.error('Error loading data:', error);
        // Set empty data on error
        setRequests([]);
        setStats({
          totalWaiting: 0,
          totalActive: 0,
          avgWaitTime: 0,
          todayHandled: 0
        });
      }
      
      setLoading(false);
    };

    loadData();
    
    // Poll for updates every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [agentId, agentName]);

  // Update wait times every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setRequests(prev => prev.map(request => ({
        ...request,
        waitTime: Math.max(0, Math.floor((Date.now() - new Date(request.createdAt).getTime()) / (1000 * 60)))
      })));
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <BrandingProvider templateType="business">
      <div className="min-h-screen bg-gray-50">
        <BrandedHeader 
          showLogo={true}
          size="medium"
        />
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Universal Agent Dashboard</h1>
          <p className="text-gray-600">Welcome back, {agentName}</p>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <BrandedCard className="p-4">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Waiting</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalWaiting}</p>
                </div>
              </div>
            </BrandedCard>
            
            <BrandedCard className="p-4">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Activity className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Active</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalActive}</p>
                </div>
              </div>
            </BrandedCard>
            
            <BrandedCard className="p-4">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Timer className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Avg Wait</p>
                  <p className="text-2xl font-bold text-gray-900">{formatWaitTime(stats.avgWaitTime)}</p>
                </div>
              </div>
            </BrandedCard>
            
            <BrandedCard className="p-4">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Today</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.todayHandled}</p>
                </div>
              </div>
            </BrandedCard>
          </div>

          {/* Filters and Search */}
          <BrandedCard className="p-6 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filters:</span>
              </div>
              
              <BrandedSelect
                value={filter.template}
                onChange={(e) => setFilter(prev => ({ ...prev, template: e.target.value as any }))}
                className="w-36"
                options={[
                  { value: 'all', label: 'All Templates' },
                  { value: 'legal', label: '⚖️ Legal' },
                  { value: 'support', label: '🎧 Support' },
                  { value: 'homeimprovement', label: '🏠 Home' }
                ]}
              />
              
              <BrandedSelect
                value={filter.status}
                onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value as any }))}
                className="w-32"
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'waiting', label: 'Waiting' },
                  { value: 'accepted', label: 'Accepted' },
                  { value: 'active', label: 'Active' }
                ]}
              />
              
              <BrandedSelect
                value={filter.urgency}
                onChange={(e) => setFilter(prev => ({ ...prev, urgency: e.target.value as any }))}
                className="w-36"
                options={[
                  { value: 'all', label: 'All Priority' },
                  { value: 'urgent', label: 'Urgent Only' },
                  { value: 'high', label: 'High Priority' }
                ]}
              />
              
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <BrandedInput
                    type="text"
                    placeholder="Search requests..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <BrandedButton
                onClick={async () => {
                  setLoading(true);
                  try {
                    const requestsData = await fetchUniversalRequests();
                    setRequests(requestsData);
                    
                    // Recalculate stats
                    const waitingRequests = requestsData.filter(r => r.status === 'waiting');
                    const activeRequests = requestsData.filter(r => r.status === 'active');
                    const completedToday = requestsData.filter(r => {
                      const createdAt = new Date(r.createdAt);
                      const today = new Date();
                      return createdAt.toDateString() === today.toDateString() && r.status === 'completed';
                    });
                    
                    const avgWait = waitingRequests.length > 0 
                      ? Math.round(waitingRequests.reduce((sum, r) => sum + r.waitTime, 0) / waitingRequests.length)
                      : 0;
                    
                    setStats({
                      totalWaiting: waitingRequests.length,
                      totalActive: activeRequests.length,
                      avgWaitTime: avgWait,
                      todayHandled: completedToday.length
                    });
                  } catch (error) {
                    console.error('Error refreshing data:', error);
                  } finally {
                    setLoading(false);
                  }
                }}
                className="flex items-center space-x-2"
                variant="outline"
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </BrandedButton>
            </div>
          </BrandedCard>

          {/* Requests List */}
          <div className="space-y-4">
            {loading ? (
              <BrandedCard className="p-8 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600">Loading requests...</p>
              </BrandedCard>
            ) : filteredRequests.length === 0 ? (
              <BrandedCard className="p-8 text-center">
                <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No requests found</h3>
                <p className="text-gray-600">
                  {filter.status !== 'all' || filter.template !== 'all' || searchTerm
                    ? 'Try adjusting your filters or search terms'
                    : 'All external API requests will appear here'
                  }
                </p>
              </BrandedCard>
            ) : (
              filteredRequests.map((request) => {
                const templateInfo = getTemplateInfo(request.templateType);
                const TemplateIcon = templateInfo.icon;
                const urgencyLevel = request.projectDetails.urgency || request.projectDetails.priority || 'medium';
                
                return (
                  <BrandedCard key={request.sessionId} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        {/* Template Icon */}
                        <div className={`p-3 rounded-lg ${templateInfo.bg} ${templateInfo.border} border`}>
                          <TemplateIcon className={`h-6 w-6 ${templateInfo.text}`} />
                        </div>
                        
                        {/* Request Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <div className="relative group">
                              {/* Customer Name as Main Title */}
                              <h3 className="text-lg font-bold text-gray-900 mb-1">
                                👤 {request.participantData.name || request.participantData.customer_name || 'Customer'}
                              </h3>
                              <p className="text-sm text-gray-600 mb-2">
                                {getRequestTitle(request)}
                              </p>
                              
                              {/* Hover Tooltip - Opens Upward */}
                              <div className="invisible group-hover:visible absolute z-50 left-0 bottom-full mb-2 w-80 bg-white border border-gray-200 rounded-lg shadow-xl p-4">
                                <div className="space-y-3">
                                  <div className="border-b pb-2">
                                    <h4 className="font-semibold text-gray-900 mb-1">Customer Information</h4>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                      <span className="text-gray-600">Name:</span>
                                      <p className="font-medium text-gray-900">{request.participantData.name || request.participantData.customer_name}</p>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">Email:</span>
                                      <p className="font-medium text-gray-900 break-all">{request.participantData.email || request.participantData.customer_email}</p>
                                    </div>
                                    {request.participantData.phone && (
                                      <div>
                                        <span className="text-gray-600">Phone:</span>
                                        <p className="font-medium text-gray-900">{request.participantData.phone}</p>
                                      </div>
                                    )}
                                    <div>
                                      <span className="text-gray-600">Wait Time:</span>
                                      <p className="font-medium text-red-600">{formatWaitTime(request.waitTime)}</p>
                                    </div>
                                  </div>
                                  
                                  <div className="border-t pt-2">
                                    <span className="text-gray-600">Request:</span>
                                    <p className="font-medium text-gray-900">{getRequestTitle(request)}</p>
                                  </div>
                                  
                                  {request.projectDetails.description && (
                                    <div>
                                      <span className="text-gray-600">Description:</span>
                                      <p className="text-sm text-gray-700 mt-1">{request.projectDetails.description}</p>
                                    </div>
                                  )}
                                  
                                  <div className="flex items-center justify-between pt-2 border-t">
                                    <span className="text-xs text-gray-500">Session ID: {request.sessionId}</span>
                                    <span className="text-xs text-gray-500">Created: {new Date(request.createdAt).toLocaleTimeString()}</span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-3 mt-1">
                                <BrandedBadge variant="secondary" className={templateInfo.bg}>
                                  {templateInfo.name}
                                </BrandedBadge>
                                <BrandedBadge className={getUrgencyColor(request.projectDetails.urgency, request.projectDetails.priority)}>
                                  {(urgencyLevel || 'medium').toUpperCase()}
                                </BrandedBadge>
                                {request.metadata?.source && (
                                  <span className="text-xs text-gray-500 flex items-center">
                                    <Zap className="h-3 w-3 mr-1" />
                                    {request.metadata.source}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                              <Clock className="h-4 w-4" />
                              <span className="font-medium text-red-600">{formatWaitTime(request.waitTime)}</span>
                            </div>
                          </div>
                          
                          {/* Quick Contact Info */}
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                            <div className="flex items-center space-x-2">
                              <Mail className="h-4 w-4" />
                              <span className="truncate max-w-48">{request.participantData.email || request.participantData.customer_email}</span>
                            </div>
                            {request.participantData.phone && (
                              <div className="flex items-center space-x-2">
                                <Phone className="h-4 w-4" />
                                <span>{request.participantData.phone}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Description */}
                          {request.projectDetails.description && (
                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                              {request.projectDetails.description}
                            </p>
                          )}
                          
                          {/* Additional Details */}
                          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                            <span>Category: {request.projectDetails.category}</span>
                            {request.projectDetails.budget && (
                              <span>Budget: {request.projectDetails.budget}</span>
                            )}
                            {request.projectDetails.address && (
                              <span>Location: {request.projectDetails.address}</span>
                            )}
                            <span>Created: {new Date(request.createdAt).toLocaleTimeString()}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center space-x-3 ml-4">
                        {request.status === 'waiting' && (
                          <>
                            <BrandedButton
                              onClick={() => acceptRequest(request)}
                              disabled={acceptingRequest === request.sessionId || rejectingRequest === request.sessionId}
                              className="flex items-center space-x-2"
                            >
                              {acceptingRequest === request.sessionId ? (
                                <>
                                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                                  <span>Accepting...</span>
                                </>
                              ) : (
                                <>
                                  <Video className="h-4 w-4" />
                                  <span>Accept</span>
                                </>
                              )}
                            </BrandedButton>
                            
                            <BrandedButton
                              onClick={() => rejectRequest(request)}
                              disabled={rejectingRequest === request.sessionId || acceptingRequest === request.sessionId}
                              variant="outline"
                              className="flex items-center space-x-2 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                            >
                              {rejectingRequest === request.sessionId ? (
                                <>
                                  <div className="animate-spin w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full"></div>
                                  <span>Rejecting...</span>
                                </>
                              ) : (
                                <>
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                  <span>Reject</span>
                                </>
                              )}
                            </BrandedButton>
                          </>
                        )}
                        
                        {request.status === 'accepted' && (
                          <BrandedBadge className="bg-blue-100 text-blue-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Accepted
                          </BrandedBadge>
                        )}
                        
                        {request.status === 'active' && (
                          <BrandedBadge className="bg-green-100 text-green-800">
                            <Activity className="h-3 w-3 mr-1" />
                            Active
                          </BrandedBadge>
                        )}
                        
                        <button 
                          className="p-2 text-gray-400 hover:text-gray-600"
                          aria-label="More options"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </BrandedCard>
                );
              })
            )}
          </div>
        </div>
      </div>
    </BrandingProvider>
  );
};